import { useState } from "react";
import type { LauraAudit } from "@/app/components/shared/types";
import { LauraStageShell } from "../shared/LauraStageShell";
import { RiskHeatmap, riskMatchesCell } from "../shared/RiskHeatmap";
import {
    computeRiskLevel,
    defaultP3FormData,
    type P3FormData,
    type P3Risk,
} from "./p3Types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type Props = {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: import("@/app/lib/auditflowApi").LauraStageState | null) => void;
};

const IMPACTS = ["low", "medium", "high"] as const;
const CLUSTERS = [
    { id: "revenue_integrity", label: "Revenue Integrity", color: "border-destructive/40" },
    { id: "operational_efficiency", label: "Operational Efficiency", color: "border-orange-400/60" },
    { id: "governance", label: "Governance", color: "border-primary/40" },
] as const;

const LEVEL_BADGE: Record<P3Risk["level"], "destructive" | "default" | "secondary" | "outline"> = {
    intolerable: "destructive",
    high: "default",
    moderate: "secondary",
    trivial: "outline",
};

export function LauraP3Workspace(props: Props) {
    const { t } = useTranslation();
    const [selectedCell, setSelectedCell] = useState<string | null>(null);

    return (
        <LauraStageShell<P3FormData>
            audit={props.audit}
            stageId="p3"
            deliverableContent={props.deliverableContent}
            onDeliverableSaved={props.onDeliverableSaved}
            onStageStateChange={props.onStageStateChange}
            defaultForm={defaultP3FormData}
            title={t("laura", "p3Title")}
            description="ECA risk assessment with impact × probability heatmap"
            generateLabel={t("laura", "generateRisks")}
        >
            {({ form, setForm }) => (
                <div className="flex flex-col gap-4">
                    <RiskHeatmap
                        risks={form.risks}
                        selectedCell={selectedCell}
                        onSelectCell={setSelectedCell}
                    />

                    <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-muted-foreground">
                            {form.risks.length} risks · click heatmap cells to filter
                        </p>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                const n = form.risks.length + 1;
                                const code = `RIS-${String(n).padStart(2, "0")}`;
                                setForm((p) => ({
                                    ...p,
                                    risks: [
                                        ...p.risks,
                                        {
                                            code,
                                            summary: "",
                                            causes: [],
                                            event: "",
                                            consequences: "",
                                            citizen_impact: "",
                                            impact: "medium",
                                            probability: "medium",
                                            level: "moderate",
                                            dimension: "",
                                            cluster: "governance",
                                            audit_response: "",
                                        },
                                    ],
                                }));
                            }}
                        >
                            + {t("laura", "addRisk")}
                        </Button>
                    </div>

                    <RiskSummaryTable risks={form.risks} />

                    {CLUSTERS.map((cluster) => {
                        const risks = form.risks.filter((r) => r.cluster === cluster.id);
                        if (risks.length === 0) return null;
                        return (
                            <Card key={cluster.id} className={cn("border-l-4", cluster.color)}>
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm">{cluster.label}</CardTitle>
                                </CardHeader>
                                <CardContent className="flex flex-col gap-2">
                                    {risks.map((risk) => (
                                        <RiskCard
                                            key={risk.code}
                                            risk={risk}
                                            highlighted={
                                                selectedCell
                                                    ? riskMatchesCell(risk, selectedCell)
                                                    : true
                                            }
                                            onChange={(r) => {
                                                const risks = form.risks.map((x) =>
                                                    x.code === risk.code ? r : x,
                                                );
                                                setForm((p) => ({ ...p, risks }));
                                            }}
                                            onRemove={() =>
                                                setForm((p) => ({
                                                    ...p,
                                                    risks: p.risks.filter(
                                                        (x) => x.code !== risk.code,
                                                    ),
                                                }))
                                            }
                                        />
                                    ))}
                                </CardContent>
                            </Card>
                        );
                    })}

                    <PriorityTiers risks={form.risks} />
                </div>
            )}
        </LauraStageShell>
    );
}

function RiskSummaryTable({ risks }: { risks: P3Risk[] }) {
    if (risks.length === 0) return null;
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">Risk summary</CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Code</TableHead>
                            <TableHead>Summary</TableHead>
                            <TableHead>Impact</TableHead>
                            <TableHead>Prob.</TableHead>
                            <TableHead>Level</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {risks.map((r) => (
                            <TableRow key={r.code}>
                                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                                <TableCell className="max-w-xs truncate">{r.summary}</TableCell>
                                <TableCell className="capitalize">{r.impact}</TableCell>
                                <TableCell className="capitalize">{r.probability}</TableCell>
                                <TableCell>
                                    <Badge variant={LEVEL_BADGE[r.level]}>{r.level}</Badge>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

function RiskCard({
    risk,
    highlighted,
    onChange,
    onRemove,
}: {
    risk: P3Risk;
    highlighted: boolean;
    onChange: (r: P3Risk) => void;
    onRemove: () => void;
}) {
    if (!highlighted) return null;

    return (
        <div className="rounded-lg border bg-card p-3">
            <div className="flex items-center justify-between gap-2">
                <Badge variant="outline" className="font-mono">
                    {risk.code}
                </Badge>
                <Badge variant={LEVEL_BADGE[risk.level]}>{risk.level}</Badge>
                <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
                    Remove
                </Button>
            </div>
            <FieldGroup className="mt-2">
                <Field>
                    <FieldLabel>Summary</FieldLabel>
                    <Input
                        value={risk.summary}
                        onChange={(e) => onChange({ ...risk, summary: e.target.value })}
                    />
                </Field>
                <Field>
                    <FieldLabel>Audit response</FieldLabel>
                    <Textarea
                        rows={2}
                        className="text-xs"
                        value={risk.audit_response}
                        onChange={(e) =>
                            onChange({ ...risk, audit_response: e.target.value })
                        }
                    />
                </Field>
                <div className="flex gap-2">
                    <Field className="flex-1">
                        <FieldLabel>Impact</FieldLabel>
                        <select
                            className="h-9 w-full rounded-md border px-2 text-sm"
                            value={risk.impact}
                            onChange={(e) => {
                                const impact = e.target.value as P3Risk["impact"];
                                onChange({
                                    ...risk,
                                    impact,
                                    level: computeRiskLevel(impact, risk.probability),
                                });
                            }}
                        >
                            {IMPACTS.map((i) => (
                                <option key={i} value={i}>
                                    {i}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field className="flex-1">
                        <FieldLabel>Probability</FieldLabel>
                        <select
                            className="h-9 w-full rounded-md border px-2 text-sm"
                            value={risk.probability}
                            onChange={(e) => {
                                const probability = e.target.value as P3Risk["probability"];
                                onChange({
                                    ...risk,
                                    probability,
                                    level: computeRiskLevel(risk.impact, probability),
                                });
                            }}
                        >
                            {IMPACTS.map((i) => (
                                <option key={i} value={i}>
                                    {i}
                                </option>
                            ))}
                        </select>
                    </Field>
                </div>
            </FieldGroup>
        </div>
    );
}

function PriorityTiers({ risks }: { risks: P3Risk[] }) {
    const tiers = [
        { label: "Tier 1 — Must Address", level: "intolerable", border: "border-destructive/50 bg-destructive/5" },
        { label: "Tier 2 — Address", level: "high", border: "border-orange-400/50 bg-orange-50/50 dark:bg-orange-950/20" },
        { label: "Tier 3 — Monitor", level: "moderate", border: "border-amber-400/50 bg-amber-50/50 dark:bg-amber-950/20" },
    ];
    return (
        <div className="flex flex-col gap-2">
            {tiers.map((t) => (
                <Card key={t.level} className={t.border}>
                    <CardContent className="flex flex-col gap-2 pt-4">
                        <p className="text-xs font-medium">{t.label}</p>
                        <div className="flex flex-wrap gap-1">
                            {risks
                                .filter((r) => r.level === t.level)
                                .map((r) => (
                                    <Badge key={r.code} variant="outline" className="font-mono">
                                        {r.code} → P4
                                    </Badge>
                                ))}
                        </div>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
